// Where the FFL mobile app lives, for the website's app banner, footer and the
// /get-app link behind the footer QR code.
//
// The App Store link carries no country, so Apple sends each visitor to their
// own store. Setting PLAY_STORE_URL to null would take Android back out of the
// banner, the footer and /get-app in one place.
export const APP_STORE_URL = 'https://apps.apple.com/app/id6800694697';
export const PLAY_STORE_URL: string | null =
  'https://play.google.com/store/apps/details?id=com.fragsforless.fragsforless';

// Apple credits a download to a campaign only through a link carrying the
// account's provider token (pt), shown in App Store Connect when you generate
// a campaign link. Set to null, App Store links go out untagged and Apple
// files website downloads under Web Referrer instead. Campaign names (ct)
// are capped at 30 characters by App Store Connect.
export const APP_STORE_PROVIDER_TOKEN: string | null = '127611413';

// A dismissed banner stays away for this long on that device.
export const APP_BANNER_DISMISS_KEY = 'ffl-app-banner-dismissed';
export const APP_BANNER_SNOOZE_DAYS = 30;

export type AppStore = 'app_store' | 'google_play';
/** Where a store link sits: on the website (banner, footer, the QR code, the
    scrolling strip under the header) or on a partner's wholesale page (NG,
    Absolu Fragrance). A bare /get-app is the QR code. */
export type AppLinkPlacement = 'banner' | 'footer' | 'qr' | 'ticker' | 'ng_wholesale' | 'af_wholesale';

// The /get-app?from= values and where each one counts. The partner pages link
// to /get-app?store=ios|android&from=<their from>: NG (Safras, 9 Oct 2026) and
// Absolu Fragrance (10 Oct 2026). A new partner page is one line here and in
// PARTNER_CAMPAIGN; the database accepts any short lowercase placement.
const PLACEMENT_BY_FROM = new Map<string, AppLinkPlacement>([
  ['banner', 'banner'],
  ['footer', 'footer'],
  ['ticker', 'ticker'],
  ['ng-wholesale', 'ng_wholesale'],
  ['af-wholesale', 'af_wholesale'],
]);

// Each partner page is a campaign of its own in App Store Connect and the
// Play Console, named after its from= value.
const PARTNER_CAMPAIGN: Partial<Record<AppLinkPlacement, string>> = {
  ng_wholesale: 'ng-wholesale',
  af_wholesale: 'af-wholesale',
};

/** Where a /get-app tap counts. Anything unknown is the QR code. */
export function placementFromParam(from: string | null): AppLinkPlacement {
  return (from ? PLACEMENT_BY_FROM.get(from) : undefined) ?? 'qr';
}

/** The website's own link to a store. /get-app counts the tap, then sends the
    visitor to the store with storeLink(). */
export function getAppHref(store: 'ios' | 'android', from: 'banner' | 'footer'): string {
  return `/get-app?store=${store}&from=${from}`;
}

// The campaign each placement is filed under in App Store Connect (ct) and the
// Play Console (referrer). The website's own buttons are "website-<placement>"
// with utm_source fragsforless.com; each partner page is a campaign of its own,
// so both consoles count its downloads apart.
function campaignFor(placement: AppLinkPlacement): { ct: string; referrer: string } {
  const partner = PARTNER_CAMPAIGN[placement];
  if (partner) {
    return {
      ct: partner,
      referrer: `utm_source=${partner}&utm_medium=referral&utm_campaign=${partner}`,
    };
  }
  return {
    ct: `website-${placement}`,
    referrer: `utm_source=fragsforless.com&utm_medium=${placement}&utm_campaign=website`,
  };
}

/** The store listing, tagged so Apple's and Google's consoles can say which
    downloads came from where. */
export function storeLink(store: AppStore, placement: AppLinkPlacement): string {
  const { ct, referrer } = campaignFor(placement);
  if (store === 'google_play' && PLAY_STORE_URL) {
    return `${PLAY_STORE_URL}&referrer=${encodeURIComponent(referrer)}`;
  }
  if (!APP_STORE_PROVIDER_TOKEN) return APP_STORE_URL;
  // Apple's campaign link format, as App Store Connect generates it.
  return `https://apps.apple.com/app/apple-store/id6800694697?pt=${APP_STORE_PROVIDER_TOKEN}&ct=${ct}&mt=8`;
}
